import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { PAYROLL_HOURS_REVIEW_MAX_DAYS, addDays, formatMinutes } from "@torre/work-hours";
import { useTheme } from "../../../context/ThemeContext";
import { presetRange, today } from "../../work-hours/lib/heatmap";
import { fetchHoursReview, type HoursReviewResponse, type HoursReviewRow } from "../services/hoursReviewService";
import { buildThemeClasses, formatAmount } from "../theme";

// ---------------------------------------------------------------------------
// Horas por colaborador (#1346) — la tabla de «Autorización de incidencias».
//
// Por colaborador y para el periodo elegido (hoy, 7 días, quincena en curso, un
// mes o un rango): cuánto REGISTRÓ en entradas de tiempo, cuánto de eso está
// JUSTIFICADO, cuánto espera APROBACIÓN, cuánto NO cuenta, la DIFERENCIA contra
// lo esperado (pérdida si faltan horas, ganancia si sobran) y el tiempo cargado
// a cada repo.
//
// «Justificadas» no es una definición de esta pantalla: es la cuenta de las
// pérdidas de Gastos (#1177) — las horas que cuentan en Horas Trabajadas:
// entradas que no necesitaron aprobación o ya fueron aprobadas (#1281), más los
// días sin entradas estimados por commits. El servidor la calcula; aquí se pinta.
//
// La pérdida o ganancia EN DINERO lleva sueldos: el servidor solo la manda a los
// dueños (`cost`). Un tech lead ve las mismas filas, en horas.
// ---------------------------------------------------------------------------

type Preset = "today" | "7d" | "qCurrent" | "month" | "custom";
const PRESETS: Preset[] = ["today", "7d", "qCurrent", "month", "custom"];

/** Sección hermana donde se resuelve lo pendiente. Literal: `config.tsx` importa las secciones (ciclo). */
const HOURS_APPROVAL_ROUTE = "/payroll/hours-approval";

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const MONTH_RE = /^\d{4}-\d{2}$/;

/** Primer y último día de un mes `YYYY-MM`. */
export function monthRange(month: string): { from: string; to: string } {
  const [y, m] = month.split("-").map(Number);
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return { from: `${month}-01`, to: `${month}-${String(last).padStart(2, "0")}` };
}

/** Horas decimales en `h:mm`, con signo si se pide («−31:15», «+5:00»). */
function hoursLabel(hours: number, signed = false): string {
  const minutes = Math.round(Math.abs(hours) * 60);
  const sign = !signed || minutes === 0 ? "" : hours < 0 ? "−" : "+";
  return `${sign}${formatMinutes(minutes)}`;
}

const OUTCOME_TONES: Record<string, string> = {
  loss: "bg-red-500/15 text-red-500",
  gain: "bg-emerald-500/15 text-emerald-600",
  even: "bg-gray-400/15 text-gray-500",
  none: "bg-gray-400/15 text-gray-500",
};

export default function HoursReviewPanel() {
  const { t } = useTranslation("common");
  const { isDarkMode } = useTheme();
  const tc = buildThemeClasses(isDarkMode);

  const [preset, setPreset] = useState<Preset>("qCurrent");
  const [month, setMonth] = useState(() => today().slice(0, 7));
  const [customFrom, setCustomFrom] = useState(() => presetRange("7d").from);
  const [customTo, setCustomTo] = useState(() => today());
  const [data, setData] = useState<HoursReviewResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<Set<string>>(new Set());

  /** Rango efectivo, o `null` si lo que hay en los campos todavía no es un rango válido. */
  const range = useMemo(() => {
    if (preset === "month") return MONTH_RE.test(month) ? monthRange(month) : null;
    if (preset === "custom") return DATE_RE.test(customFrom) && DATE_RE.test(customTo) && customFrom <= customTo ? { from: customFrom, to: customTo } : null;
    return presetRange(preset);
  }, [preset, month, customFrom, customTo]);
  const from = range?.from ?? null;
  const to = range?.to ?? null;

  // El servidor acepta hasta 366 días: pasarse es un error del rango, no de la red.
  const tooLong = Boolean(from && to && addDays(from, PAYROLL_HOURS_REVIEW_MAX_DAYS - 1) < to);

  // Cada carga lleva su número: si el periodo cambia mientras otra viaja, la
  // respuesta vieja se descarta al llegar en vez de pisar los números del
  // periodo que la persona está viendo.
  const requestId = useRef(0);
  const load = useCallback(async () => {
    const id = ++requestId.current;
    if (!from || !to || tooLong) {
      // Sin rango válido no hay números que enseñar: los anteriores, junto al
      // aviso de «rango inválido», se leerían como si fueran de ese rango.
      setData(null);
      setError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const next = await fetchHoursReview({ from, to });
      if (id !== requestId.current) return;
      setData(next);
    } catch (err) {
      if (id !== requestId.current) return;
      // Los números que hubiera eran de otro periodo.
      setData(null);
      const code = (err as { response?: { data?: { error?: { code?: unknown } } } })?.response?.data?.error?.code;
      setError(t(code === "RANGE_TOO_LONG" ? "payroll.hoursReview.rangeTooLong" : "payroll.hoursReview.loadError", { days: PAYROLL_HOURS_REVIEW_MAX_DAYS }));
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }, [from, to, tooLong, t]);

  // Al desmontar, cualquier respuesta en vuelo queda vieja.
  useEffect(
    () => () => {
      requestId.current += 1;
    },
    [],
  );

  useEffect(() => {
    void load();
  }, [load]);

  const toggle = (key: string) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const rows = data?.rows ?? [];
  const totals = data?.totals;
  const cost = data?.canSeeAmounts ? data.cost : undefined;
  const currencies = useMemo(() => Object.keys(cost?.byCurrency ?? {}).sort(), [cost]);
  const moneyTotal = (field: "lossCost" | "gainCost") =>
    currencies
      .filter((c) => (cost?.byCurrency[c][field] ?? 0) > 0)
      .map((c) => formatAmount(cost?.byCurrency[c][field], c))
      .join(" · ") || "—";

  const mins = (m: number) => `${formatMinutes(m)} h`;
  const cols = 8 + (cost ? 1 : 0);

  const moneyCell = (row: HoursReviewRow) => {
    const line = cost?.byRow[row.key];
    if (!line) return <span className={tc.textSecondary}>—</span>;
    if (line.lossCost > 0) return <span className="font-semibold text-red-500">−{formatAmount(line.lossCost, line.currency)}</span>;
    if (line.gainCost > 0) return <span className="font-semibold text-emerald-600">+{formatAmount(line.gainCost, line.currency)}</span>;
    return <span className={tc.textSecondary}>{formatAmount(0, line.currency)}</span>;
  };

  return (
    <section className={`mb-6 rounded-2xl border p-4 ${tc.cardBorder} ${tc.cardBg}`} data-testid="hours-review">
      <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
        <div className="max-w-3xl">
          <h2 className={`flex items-center gap-2 text-base font-semibold ${tc.text}`}>
            <i className="ri-scales-3-line text-violet-500" />
            {t("payroll.hoursReview.title")}
          </h2>
          <p className={`mt-0.5 text-xs ${tc.textSecondary}`}>{t("payroll.hoursReview.subtitle")}</p>
          <p className={`mt-1 text-[11px] ${tc.textSecondary}`} data-testid="hours-review-definition">
            <i className="ri-information-line mr-1" />
            {t("payroll.hoursReview.definition")}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-1" role="tablist" aria-label={t("payroll.hoursReview.period")}>
          {PRESETS.map((p) => (
            <button
              key={p}
              type="button"
              role="tab"
              aria-selected={preset === p}
              onClick={() => setPreset(p)}
              data-testid={`hours-review-preset-${p}`}
              className={`rounded-lg border px-2.5 py-1 text-xs ${preset === p ? "border-violet-500 bg-violet-500/10 text-violet-500" : `${tc.cardBorder} ${tc.textSecondary}`}`}
            >
              {t(`payroll.hoursReview.presets.${p}`)}
            </button>
          ))}
        </div>
      </div>

      <div className={`mb-3 flex flex-wrap items-end gap-3 text-xs ${tc.textSecondary}`}>
        {preset === "month" && (
          <label className="flex flex-col gap-1">
            {t("payroll.hoursReview.month")}
            <input
              type="month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              data-testid="hours-review-month"
              className={`rounded-lg border px-2 py-1 text-sm ${tc.cardBorder} ${tc.cardBg} ${tc.text}`}
            />
          </label>
        )}
        {preset === "custom" && (
          <>
            <label className="flex flex-col gap-1">
              {t("payroll.hoursReview.from")}
              <input
                type="date"
                value={customFrom}
                max={customTo || undefined}
                onChange={(e) => setCustomFrom(e.target.value)}
                data-testid="hours-review-from"
                className={`rounded-lg border px-2 py-1 text-sm ${tc.cardBorder} ${tc.cardBg} ${tc.text}`}
              />
            </label>
            <label className="flex flex-col gap-1">
              {t("payroll.hoursReview.to")}
              <input
                type="date"
                value={customTo}
                min={customFrom || undefined}
                onChange={(e) => setCustomTo(e.target.value)}
                data-testid="hours-review-to"
                className={`rounded-lg border px-2 py-1 text-sm ${tc.cardBorder} ${tc.cardBg} ${tc.text}`}
              />
            </label>
          </>
        )}
        {range && tooLong ? (
          <span className="text-amber-600" data-testid="hours-review-range-too-long">
            {t("payroll.hoursReview.rangeTooLong", { days: PAYROLL_HOURS_REVIEW_MAX_DAYS })}
          </span>
        ) : range ? (
          <span data-testid="hours-review-range">
            {t("payroll.hoursReview.range", { from: range.from, to: range.to })}
            {data?.rule.businessDays != null && !loading && <> · {t("payroll.hoursReview.expectedNote", { date: data.rule.expectedTo, days: data.rule.businessDays })}</>}
          </span>
        ) : (
          <span className="text-amber-600" data-testid="hours-review-invalid-range">
            {t("payroll.hoursReview.invalidRange")}
          </span>
        )}
        {data && data.scope === "squads" && <span data-testid="hours-review-scope">{t("payroll.hoursReview.scopeSquads")}</span>}
        {data && data.scope === "own" && <span data-testid="hours-review-scope">{t("payroll.hoursReview.scopeOwn")}</span>}
      </div>

      {error && (
        <div className="mb-3 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-2 text-sm text-red-500" data-testid="hours-review-error">
          <i className="ri-error-warning-line mr-1" />
          {error}
        </div>
      )}
      {data?.truncated && (
        <div className="mb-3 rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-2 text-sm text-amber-600" data-testid="hours-review-truncated">
          <i className="ri-alert-line mr-1" />
          {t("payroll.hoursReview.truncated")}
        </div>
      )}

      {totals && !error && (
        <div className="mb-3 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6" data-testid="hours-review-totals">
          {(
            [
              ["registered", mins(totals.registeredMinutes), ""],
              ["justified", mins(totals.justifiedMinutes), "text-emerald-600"],
              ["pending", mins(totals.pendingMinutes), totals.pendingMinutes > 0 ? "text-amber-600" : ""],
              ["unjustified", mins(totals.unjustifiedMinutes), totals.unjustifiedMinutes > 0 ? "text-red-500" : ""],
              ["loss", `${hoursLabel(totals.lossHours)} h`, totals.lossHours > 0 ? "text-red-500" : ""],
              ["gain", `${hoursLabel(totals.gainHours)} h`, totals.gainHours > 0 ? "text-emerald-600" : ""],
            ] as const
          ).map(([key, value, tone]) => (
            <div key={key} className={`rounded-xl border p-3 ${tc.cardBorder}`} data-testid={`hours-review-total-${key}`}>
              <p className={`text-[11px] ${tc.textSecondary}`}>{t(`payroll.hoursReview.cards.${key}`)}</p>
              <p className={`text-lg font-bold ${tone || tc.text}`}>{value}</p>
              {/* El dinero solo existe si el servidor lo mandó (dueños). */}
              {cost && key === "loss" && <p className="text-[11px] text-red-500" data-testid="hours-review-loss-money">{moneyTotal("lossCost")}</p>}
              {cost && key === "gain" && <p className="text-[11px] text-emerald-600" data-testid="hours-review-gain-money">{moneyTotal("gainCost")}</p>}
            </div>
          ))}
        </div>
      )}

      {data && !error && (
        <div className={`mb-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px] ${tc.textSecondary}`}>
          {!data.canSeeAmounts && <span data-testid="hours-review-owners-only">{t("payroll.hoursReview.ownersOnly")}</span>}
          {cost && cost.unpriced.length > 0 && <span data-testid="hours-review-unpriced">{t("payroll.hoursReview.unpriced", { count: cost.unpriced.length })}</span>}
          {totals && totals.pendingMinutes > 0 && (
            <Link to={HOURS_APPROVAL_ROUTE} className="text-violet-500 underline" data-testid="hours-review-pending-link">
              {t("payroll.hoursReview.pendingLink", { hours: formatMinutes(totals.pendingMinutes) })}
            </Link>
          )}
        </div>
      )}

      {loading && !data && <p className={`text-sm ${tc.textSecondary}`}>…</p>}
      {!error && data && rows.length === 0 && (
        <p className={`text-sm ${tc.textSecondary}`} data-testid="hours-review-empty">
          {t("payroll.hoursReview.empty")}
        </p>
      )}

      {!error && rows.length > 0 && (
        <div className={`overflow-x-auto ${loading ? "opacity-60" : ""}`}>
          <table className="w-full text-sm">
            <thead>
              <tr className={`text-left text-xs ${tc.textSecondary}`}>
                <th className="py-1 pr-2">{t("payroll.hoursReview.cols.person")}</th>
                <th className="py-1 pr-2 text-right" title={t("payroll.hoursReview.hints.registered")}>{t("payroll.hoursReview.cols.registered")}</th>
                <th className="py-1 pr-2 text-right" title={t("payroll.hoursReview.hints.justified")}>{t("payroll.hoursReview.cols.justified")}</th>
                <th className="py-1 pr-2 text-right" title={t("payroll.hoursReview.hints.pending")}>{t("payroll.hoursReview.cols.pending")}</th>
                <th className="py-1 pr-2 text-right" title={t("payroll.hoursReview.hints.unjustified")}>{t("payroll.hoursReview.cols.unjustified")}</th>
                <th className="py-1 pr-2 text-right" title={t("payroll.hoursReview.hints.expected")}>{t("payroll.hoursReview.cols.expected")}</th>
                <th className="py-1 pr-2 text-right" title={t("payroll.hoursReview.hints.diff")}>{t("payroll.hoursReview.cols.diff")}</th>
                {cost && <th className="py-1 pr-2 text-right">{t("payroll.hoursReview.cols.money")}</th>}
                <th className="py-1 pr-2">{t("payroll.hoursReview.cols.repos")}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <Fragment key={r.key}>
                  <tr className={`border-t ${tc.cardBorder} ${tc.text}`} data-testid={`hours-review-row-${r.key}`}>
                    <td className="py-1.5 pr-2 font-medium">
                      {r.name}
                      {r.own && <span className="ml-1 rounded-full bg-violet-500/15 px-1.5 py-0.5 text-[10px] font-normal text-violet-500">{t("payroll.hoursReview.you")}</span>}
                    </td>
                    <td className="py-1.5 pr-2 text-right whitespace-nowrap" data-testid={`hours-review-registered-${r.key}`}>{mins(r.registeredMinutes)}</td>
                    <td className="py-1.5 pr-2 text-right whitespace-nowrap" data-testid={`hours-review-justified-${r.key}`}>
                      <span className="font-semibold text-emerald-600">{mins(r.justifiedMinutes)}</span>
                      {r.estimatedMinutes > 0 && (
                        <span className={`block text-[11px] ${tc.textSecondary}`}>
                          {t("payroll.hoursReview.justifiedSplit", { entries: formatMinutes(r.entryMinutes), estimated: formatMinutes(r.estimatedMinutes) })}
                        </span>
                      )}
                    </td>
                    <td className={`py-1.5 pr-2 text-right whitespace-nowrap ${r.pendingMinutes > 0 ? "font-semibold text-amber-600" : tc.textSecondary}`} data-testid={`hours-review-pending-${r.key}`}>
                      {mins(r.pendingMinutes)}
                    </td>
                    <td className={`py-1.5 pr-2 text-right whitespace-nowrap ${r.unjustifiedMinutes > 0 ? "font-semibold text-red-500" : tc.textSecondary}`} data-testid={`hours-review-unjustified-${r.key}`}>
                      {mins(r.unjustifiedMinutes)}
                    </td>
                    <td className="py-1.5 pr-2 text-right whitespace-nowrap">{r.expectedHours === null ? "—" : `${hoursLabel(r.expectedHours)} h`}</td>
                    <td className="py-1.5 pr-2 text-right whitespace-nowrap" data-testid={`hours-review-diff-${r.key}`}>
                      {r.diffHours !== null && <span className="mr-1 font-semibold">{hoursLabel(r.diffHours, true)} h</span>}
                      <span className={`rounded-md px-1.5 py-0.5 text-[11px] font-medium ${OUTCOME_TONES[r.outcome]}`}>{t(`payroll.hoursReview.outcome.${r.outcome}`)}</span>
                    </td>
                    {cost && (
                      <td className="py-1.5 pr-2 text-right whitespace-nowrap" data-testid={`hours-review-money-${r.key}`}>
                        {moneyCell(r)}
                      </td>
                    )}
                    <td className="py-1.5 pr-2 whitespace-nowrap">
                      {r.byRepo.length > 0 ? (
                        <button
                          type="button"
                          onClick={() => toggle(r.key)}
                          aria-expanded={open.has(r.key)}
                          aria-label={`repos-${r.key}`}
                          className="rounded-md border border-violet-500/40 px-2 py-0.5 text-[11px] text-violet-500 hover:bg-violet-500/10"
                        >
                          <i className={`${open.has(r.key) ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} mr-0.5`} />
                          {t("payroll.hoursReview.reposCount", { count: r.byRepo.length })}
                        </button>
                      ) : (
                        <span className={tc.textSecondary}>—</span>
                      )}
                    </td>
                  </tr>
                  {open.has(r.key) && (
                    <tr className={tc.text} data-testid={`hours-review-repos-${r.key}`}>
                      <td colSpan={cols} className="pb-3 pl-4 pr-2">
                        <table className={`w-full rounded-lg text-xs ${isDarkMode ? "bg-white/5" : "bg-gray-50"}`}>
                          <thead>
                            <tr className={`text-left ${tc.textSecondary}`}>
                              <th className="px-2 py-1">{t("payroll.hoursReview.repoCols.repo")}</th>
                              <th className="px-2 py-1 text-right">{t("payroll.hoursReview.repoCols.justified")}</th>
                              <th className="px-2 py-1 text-right">{t("payroll.hoursReview.repoCols.entries")}</th>
                              <th className="px-2 py-1 text-right">{t("payroll.hoursReview.repoCols.estimated")}</th>
                              <th className="px-2 py-1 text-right">{t("payroll.hoursReview.repoCols.pending")}</th>
                              <th className="px-2 py-1 text-right">{t("payroll.hoursReview.repoCols.unjustified")}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {r.byRepo.map((l) => (
                              <tr key={l.repo || "-"} data-testid={`hours-review-repo-${r.key}-${l.repo || "none"}`}>
                                <td className="px-2 py-1 font-mono">{l.repo || <span className="font-sans italic">{t("payroll.hoursReview.noRepo")}</span>}</td>
                                <td className="px-2 py-1 text-right font-semibold">{mins(l.justifiedMinutes)}</td>
                                <td className="px-2 py-1 text-right">{mins(l.entryMinutes)}</td>
                                <td className="px-2 py-1 text-right">{mins(l.estimatedMinutes)}</td>
                                <td className="px-2 py-1 text-right">{mins(l.pendingMinutes)}</td>
                                <td className="px-2 py-1 text-right">{mins(l.unjustifiedMinutes)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
